import React, { useState, useEffect } from 'react';
import { useDarkMode } from '../../../../context/DarkModeContext';
import { InternshipResult } from '../../../../types/internship';
import { CareerFocusWithMetrics } from '../../../../hooks/useCareerFocus';

interface NoOfferFormProps {
  data: Partial<InternshipResult>;
  onUpdate: (updates: Partial<InternshipResult>) => void;
  focus: CareerFocusWithMetrics;
}

const NoOfferForm: React.FC<NoOfferFormProps> = ({
  data,
  onUpdate
}) => {
  const { darkMode } = useDarkMode();
  const [feedbackReceived, setFeedbackReceived] = useState(data.feedbackReceived || '');
  const [rejectionStage, setRejectionStage] = useState(data.rejectionStage || '');
  const [rejectionReason, setRejectionReason] = useState(data.rejectionReason || '');
  
  const bgColor = darkMode ? 'bg-gray-700' : 'bg-gray-50';
  const textColor = darkMode ? 'text-gray-100' : 'text-gray-900';
  const inputBg = darkMode ? 'bg-gray-800' : 'bg-white';
  const borderColor = darkMode ? 'border-gray-600' : 'border-gray-300';
  const mutedTextColor = darkMode ? 'text-gray-400' : 'text-gray-600';
  
  // Update parent component whenever state changes
  useEffect(() => {
    // Only update if values have actually changed from what was passed in
    const hasChanges = 
      feedbackReceived !== (data.feedbackReceived || '') ||
      rejectionStage !== (data.rejectionStage || '') ||
      rejectionReason !== (data.rejectionReason || '');
    
    if (hasChanges) {
      onUpdate({
        feedbackReceived,
        rejectionStage,
        rejectionReason
      });
    }
  }, [feedbackReceived, rejectionStage, rejectionReason]);
  
  return (
    <div className="space-y-6">
      <div>
        <h2 className={`text-xl font-semibold mb-2 ${textColor}`}>
          Feedback & Learning Opportunity
        </h2>
        <p className={mutedTextColor}>
          We know this can be disappointing. Let's capture any feedback to help you grow.
        </p>
      </div>
      
      {/* At what stage */}
      <div>
        <label className={`block text-sm font-medium mb-2 ${textColor}`}>
          At what stage did you receive this outcome?
        </label>
        <select
          value={rejectionStage}
          onChange={(e) => setRejectionStage(e.target.value)}
          className={`w-full px-3 py-2 rounded-lg border ${borderColor} ${inputBg} ${textColor}`}
        >
          <option value="">Select...</option>
          <option value="after_final_interview">After final interview</option>
          <option value="after_project_presentation">After project presentation</option>
          <option value="end_of_internship">End of internship</option>
          <option value="mid_internship">Mid-internship review</option>
          <option value="other">Other</option>
        </select>
      </div>
      
      {/* Feedback Received */}
      <div>
        <label className={`block text-sm font-medium mb-2 ${textColor}`}>
          Did you receive any feedback? (Optional)
        </label>
        <textarea
          value={feedbackReceived}
          onChange={(e) => setFeedbackReceived(e.target.value)}
          rows={4}
          placeholder="Share any feedback you received from your manager, mentor, or HR..."
          className={`w-full px-3 py-2 rounded-lg border ${borderColor} ${inputBg} ${textColor} placeholder-gray-500`}
        />
        <p className={`text-xs mt-1 ${mutedTextColor}`}>
          This will remain private and help you track areas for improvement
        </p>
      </div>
      
      {/* Reason (if known) */}
      <div>
        <label className={`block text-sm font-medium mb-2 ${textColor}`}>
          Primary reason (if shared)
        </label>
        <div className="space-y-2">
          {[
            { value: 'headcount', label: 'No headcount/budget constraints' },
            { value: 'performance', label: 'Performance did not meet expectations' },
            { value: 'fit', label: 'Not the right fit for the team/role' },
            { value: 'business_needs', label: 'Changing business needs' },
            { value: 'not_shared', label: 'Reason not shared' },
            { value: 'other', label: 'Other' }
          ].map(option => (
            <label key={option.value} className="flex items-center">
              <input
                type="radio"
                value={option.value}
                checked={rejectionReason === option.value}
                onChange={(e) => setRejectionReason(e.target.value)}
                className="mr-3"
              />
              <span className={textColor}>{option.label}</span>
            </label>
          ))}
        </div>
      </div>
      
      {/* Encouragement */}
      <div className={`p-4 rounded-lg ${bgColor}`}>
        <p className={`text-sm ${textColor}`}>
          💡 Remember: Every experience is a learning opportunity. The feedback and insights you gain here will help you succeed in future opportunities.
        </p>
      </div>
    </div>
  );
};

export default NoOfferForm;